import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@arcadeai/design-system/components/ui/atoms/command";
import { useSidebar } from "@arcadeai/design-system/components/ui/atoms/sidebar";
import { useNavigate } from "@tanstack/react-router";
import {
  Cloud,
  Fingerprint,
  KeyRound,
  Lock,
  Settings,
  Sparkles,
  User,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTheme } from "@/components/custom/theme/theme-provider";
import { buildOrgProjectPath } from "@/lib/org-project-context/constants";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";

export function DashboardCommandDialog() {
  const [open, setOpen] = useState(false);

  const { setTheme, theme } = useTheme();
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const navigate = useNavigate();
  const scope = useOrgProjectRouteContext();

  const navigateTo = (suffix: string) => {
    navigate({ to: buildOrgProjectPath(suffix, scope) });
    setOpen(false);
  };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((isOpen) => !isOpen);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  return (
    <>
      <Button
        className="hidden text-muted-foreground text-sm hover:bg-transparent active:bg-transparent md:block"
        onClick={() => setOpen(true)}
        variant="ghost"
      >
        Press{" "}
        <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-medium font-mono text-[10px] text-muted-foreground opacity-100">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>
      <CommandDialog onOpenChange={setOpen} open={open}>
        <CommandInput placeholder="Type a command or search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigation">
            <CommandItem onSelect={() => navigateTo("/playground")}>
              <Sparkles className="mr-2 size-4" />
              <span>Playground</span>
            </CommandItem>
            <CommandItem onSelect={() => navigateTo("/auth/oauth")}>
              <Fingerprint className="mr-2 size-4" />
              <span>OAuth Providers</span>
            </CommandItem>
            <CommandItem onSelect={() => navigateTo("/auth/secrets")}>
              <Lock className="mr-2 size-4" />
              <span>Secrets</span>
            </CommandItem>
            <CommandItem onSelect={() => navigateTo("/tools")}>
              <Wrench className="mr-2 size-4" />
              <span>Tools</span>
            </CommandItem>
            <CommandItem onSelect={() => navigateTo("/servers")}>
              <Cloud className="mr-2 size-4" />
              <span>Servers</span>
            </CommandItem>
            <CommandItem onSelect={() => navigateTo("/api-keys")}>
              <KeyRound className="mr-2 size-4" />
              <span>API Keys</span>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup heading="Settings">
            <CommandItem
              onSelect={() => {
                setSidebarOpen(!sidebarOpen);
                setOpen(false);
              }}
            >
              <User className="mr-2 size-4" />
              <span>Toggle Sidebar</span>
              <CommandShortcut>⌘B</CommandShortcut>
            </CommandItem>
            <CommandItem
              onSelect={() => {
                setTheme(theme === "light" ? "dark" : "light");
                setOpen(false);
              }}
            >
              <Settings className="mr-2 size-4" />
              <span>Toggle Mode</span>
              <CommandShortcut>⌘T</CommandShortcut>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
